/**
 * Journal Entries Page
 * Manual journal entry creation and general ledger view
 */

import React, { useState, useEffect } from 'react'
import { format } from 'date-fns'
import {
  BookMarked,
  Plus,
  Eye,
  X,
  Check,
  AlertCircle,
  FileText,
  Calculator,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { Separator } from '@/components/ui/separator'
import { AdminUser } from '@/lib/authService'
import LedgerService from '@/services/ledgerService'
import { ChartOfAccountsService } from '@/services/financeService'
import type { JournalEntry, JournalEntryLine, ChartOfAccount } from '@/services/financeService'
import { formatCurrency } from '@/lib/currency'

interface JournalEntriesProps {
  user: AdminUser | null
}

export default function JournalEntries({ user }: JournalEntriesProps) {
  const [entries, setEntries] = useState<JournalEntry[]>([])
  const [accounts, setAccounts] = useState<ChartOfAccount[]>([])
  const [loading, setLoading] = useState(true)
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [showViewDialog, setShowViewDialog] = useState(false)
  const [selectedEntry, setSelectedEntry] = useState<JournalEntry | null>(null)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState('')

  // Form data for new entry
  const [entryDate, setEntryDate] = useState(format(new Date(), 'yyyy-MM-dd'))
  const [entryType, setEntryType] = useState('manual')
  const [description, setDescription] = useState('')
  const [reference, setReference] = useState('')
  const [lines, setLines] = useState<Partial<JournalEntryLine>[]>([
    { account_id: '', debit_amount: 0, credit_amount: 0, description: '' },
    { account_id: '', debit_amount: 0, credit_amount: 0, description: '' },
  ])

  useEffect(() => {
    loadEntries()
    loadAccounts()
  }, [])

  async function loadEntries() {
    if (!user?.churchId) return

    setLoading(true)
    try {
      const data = await LedgerService.getJournalEntries(user.churchId, {
        includeVoided: true,
      })
      setEntries(data)
    } catch (error) {
      console.error('Error loading entries:', error)
    } finally {
      setLoading(false)
    }
  }

  async function loadAccounts() {
    if (!user?.churchId) return

    try {
      const data = await ChartOfAccountsService.getAccounts(user.churchId, {
        activeOnly: true,
      })
      setAccounts(data)
    } catch (error) {
      console.error('Error loading accounts:', error)
    }
  }

  function openAddDialog() {
    setEntryDate(format(new Date(), 'yyyy-MM-dd'))
    setDescription('')
    setReference('')
    setLines([
      { account_id: '', debit_amount: 0, credit_amount: 0, description: '' },
      { account_id: '', debit_amount: 0, credit_amount: 0, description: '' },
    ])
    setShowAddDialog(true)
  }

  function openViewDialog(entry: JournalEntry) {
    setSelectedEntry(entry)
    setShowViewDialog(true)
  }

  function addLine() {
    setLines([...lines, { account_id: '', debit_amount: 0, credit_amount: 0, description: '' }])
  }

  function removeLine(index: number) {
    if (lines.length <= 2) {
      alert('Journal entry must have at least 2 lines')
      return
    }
    setLines(lines.filter((_, i) => i !== index))
  }

  function updateLine(index: number, field: string, value: any) {
    const newLines = [...lines]
    newLines[index] = { ...newLines[index], [field]: value }
    setLines(newLines)
  }

  function calculateTotals() {
    const totalDebits = lines.reduce((sum, line) => sum + (Number(line.debit_amount) || 0), 0)
    const totalCredits = lines.reduce((sum, line) => sum + (Number(line.credit_amount) || 0), 0)
    return { totalDebits, totalCredits, difference: totalDebits - totalCredits }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!user?.churchId) return

    const { totalDebits, totalCredits, difference } = calculateTotals()

    if (Math.abs(difference) > 0.01) {
      alert(`Entry is not balanced! Debits: ${formatCurrency(totalDebits, 'GHS')}, Credits: ${formatCurrency(totalCredits, 'GHS')}`)
      return
    }

    // Validate all lines have accounts
    if (lines.some(line => !line.account_id)) {
      alert('All lines must have an account selected')
      return
    }

    try {
      const entry = await LedgerService.createJournalEntry(
        {
          church_id: user.churchId,
          entry_date: entryDate,
          entry_type: entryType as any,
          description,
          reference_number: reference || undefined,
          created_by: user.id,
        },
        lines as JournalEntryLine[]
      )

      // Auto-post the entry
      await LedgerService.postJournalEntry(entry.id)

      setShowAddDialog(false)
      loadEntries()
      alert('Journal entry created and posted successfully!')
    } catch (error: any) {
      console.error('Error creating entry:', error)
      alert(`Failed to create entry: ${error.message}`)
    }
  }

  async function handleVoidEntry(entryId: string) {
    if (!confirm('Are you sure you want to void this entry? This will reverse all account balances.')) return

    try {
      await LedgerService.voidJournalEntry(entryId, user!.id, 'Voided by user')
      loadEntries()
      setShowViewDialog(false)
      alert('Entry voided successfully')
    } catch (error: any) {
      console.error('Error voiding entry:', error)
      alert(`Failed to void entry: ${error.message}`)
    }
  }

  const filteredEntries = entries
    .filter((entry) => statusFilter === 'all' || entry.status === statusFilter)
    .filter((entry) =>
      searchTerm === '' ||
      entry.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (entry.reference_number && entry.reference_number.toLowerCase().includes(searchTerm.toLowerCase()))
    )

  function getStatusBadge(status: string) {
    const variants: Record<string, any> = {
      draft: { variant: 'outline', icon: FileText, label: 'Draft' },
      posted: { variant: 'default', icon: Check, label: 'Posted' },
      voided: { variant: 'destructive', icon: X, label: 'Voided' },
    }
    const config = variants[status] || variants.draft
    return (
      <Badge variant={config.variant} className="gap-1">
        <config.icon className="h-3 w-3" />
        {config.label}
      </Badge>
    )
  }

  const { totalDebits, totalCredits, difference } = calculateTotals()
  const isBalanced = Math.abs(difference) < 0.01

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <h1 className="text-3xl font-bold tracking-tight">Journal Entries</h1>
        <p className="text-muted-foreground">
          General ledger and manual journal entries
        </p>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <Button onClick={openAddDialog}>
          <Plus className="mr-2 h-4 w-4" />
          New Journal Entry
        </Button>

        <div className="flex gap-2">
          <Input
            placeholder="Search entries..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-[250px]"
          />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="posted">Posted</SelectItem>
              <SelectItem value="voided">Voided</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Entries Table */}
      <Card>
        <CardHeader>
          <CardTitle>Journal Entries ({filteredEntries.length})</CardTitle>
          <CardDescription>View all general ledger transactions</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading entries...</p>
              </div>
            </div>
          ) : filteredEntries.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No journal entries found.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEntries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>
                      {format(new Date(entry.entry_date), 'MMM dd, yyyy')}
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {entry.reference_number || '-'}
                    </TableCell>
                    <TableCell>{entry.description}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{entry.entry_type}</Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono">
                      {formatCurrency(entry.total_debits || 0, 'GHS')}
                    </TableCell>
                    <TableCell>{getStatusBadge(entry.status)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openViewDialog(entry)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add Entry Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Journal Entry</DialogTitle>
            <DialogDescription>
              Create a manual journal entry. Entry must be balanced (debits = credits).
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Header Info */}
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="entry_date">
                  Entry Date <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="entry_date"
                  type="date"
                  value={entryDate}
                  onChange={(e) => setEntryDate(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="entry_type">Entry Type</Label>
                <Select value={entryType} onValueChange={setEntryType}>
                  <SelectTrigger id="entry_type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual</SelectItem>
                    <SelectItem value="adjustment">Adjustment</SelectItem>
                    <SelectItem value="closing">Closing</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reference">Reference Number</Label>
                <Input
                  id="reference"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder="Optional"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">
                Description <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe the purpose of this entry"
                required
              />
            </div>

            <Separator />

            {/* Entry Lines */}
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <Label>Entry Lines</Label>
                <Button type="button" variant="outline" size="sm" onClick={addLine}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add Line
                </Button>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[300px]">Account</TableHead>
                      <TableHead className="w-[200px]">Line Description</TableHead>
                      <TableHead className="text-right w-[120px]">Debit</TableHead>
                      <TableHead className="text-right w-[120px]">Credit</TableHead>
                      <TableHead className="w-[80px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lines.map((line, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Select
                            value={line.account_id || ''}
                            onValueChange={(value) => updateLine(index, 'account_id', value)}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select account" />
                            </SelectTrigger>
                            <SelectContent>
                              {accounts.map((account) => (
                                <SelectItem key={account.id} value={account.id}>
                                  {account.account_code} - {account.account_name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Input
                            value={line.description || ''}
                            onChange={(e) => updateLine(index, 'description', e.target.value)}
                            placeholder="Optional"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={line.debit_amount || ''}
                            onChange={(e) => updateLine(index, 'debit_amount', e.target.value)}
                            placeholder="0.00"
                            className="text-right"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={line.credit_amount || ''}
                            onChange={(e) => updateLine(index, 'credit_amount', e.target.value)}
                            placeholder="0.00"
                            className="text-right"
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeLine(index)}
                            disabled={lines.length <= 2}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>

            {/* Totals */}
            <Card className={isBalanced ? 'border-green-500' : 'border-destructive'}>
              <CardContent className="pt-6">
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Total Debits:</span>
                    <span className="font-mono">{formatCurrency(totalDebits, 'GHS')}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Total Credits:</span>
                    <span className="font-mono">{formatCurrency(totalCredits, 'GHS')}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between items-center">
                    <span className="font-bold">Difference:</span>
                    <span className={`font-mono font-bold ${isBalanced ? 'text-green-600' : 'text-destructive'}`}>
                      {formatCurrency(Math.abs(difference), 'GHS')}
                    </span>
                  </div>
                  {isBalanced ? (
                    <Badge variant="default" className="w-full justify-center gap-2">
                      <Check className="h-4 w-4" />
                      Entry is balanced
                    </Badge>
                  ) : (
                    <Badge variant="destructive" className="w-full justify-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      Entry must be balanced to post
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAddDialog(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={!isBalanced}>
                <Calculator className="mr-2 h-4 w-4" />
                Create & Post Entry
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Entry Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Journal Entry Details</DialogTitle>
            <DialogDescription>
              {selectedEntry && format(new Date(selectedEntry.entry_date), 'MMMM dd, yyyy')}
            </DialogDescription>
          </DialogHeader>

          {selectedEntry && (
            <div className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label>Reference Number</Label>
                  <p className="text-sm font-mono">{selectedEntry.reference_number || '-'}</p>
                </div>
                <div>
                  <Label>Type</Label>
                  <p className="text-sm">{selectedEntry.entry_type}</p>
                </div>
                <div className="md:col-span-2">
                  <Label>Description</Label>
                  <p className="text-sm">{selectedEntry.description}</p>
                </div>
                <div>
                  <Label>Status</Label>
                  <div className="mt-1">{getStatusBadge(selectedEntry.status)}</div>
                </div>
              </div>

              <Separator />

              <div>
                <Label>Entry Lines</Label>
                <Table className="mt-2">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Account</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Debit</TableHead>
                      <TableHead className="text-right">Credit</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedEntry.lines?.map((line: any, index: number) => (
                      <TableRow key={index}>
                        <TableCell>
                          {line.account?.account_code} - {line.account?.account_name}
                        </TableCell>
                        <TableCell className="text-sm">{line.description || '-'}</TableCell>
                        <TableCell className="text-right font-mono">
                          {line.debit_amount > 0 ? formatCurrency(line.debit_amount, 'GHS') : '-'}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {line.credit_amount > 0 ? formatCurrency(line.credit_amount, 'GHS') : '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="font-bold">
                      <TableCell colSpan={2}>Total</TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(selectedEntry.total_debits || 0, 'GHS')}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(selectedEntry.total_credits || 0, 'GHS')}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              {selectedEntry.status === 'posted' && (
                <DialogFooter>
                  <Button
                    variant="destructive"
                    onClick={() => handleVoidEntry(selectedEntry.id)}
                  >
                    <X className="mr-2 h-4 w-4" />
                    Void Entry
                  </Button>
                </DialogFooter>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
